import { useMemo } from 'react';
import { SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import { type SpreadsheetColumn } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { useBoundaryTerritoryGroupsGroupedByBoundaryGroupSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  type BoundaryTerritoryGroup, type BoundaryTerritoryGroupSettings,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { matchBoundaryGroupDemographic } from './demographicsMetric.factory';
import { type MetricModel } from './metrics.factory';
import { areMetricsTheSame } from './metrics.helpers';
import { MetricsDataAction } from './metrics.types';

export const useBoundaryMetrics = (
  activeBoundaryTerritoryGroup: BoundaryTerritoryGroup | undefined,
) => {
  const boundaryGroups = useBoundaryGroupsSelector();
  const spreadsheetColumns = useSpreadsheetColumns();
  const allFillGroupedByBoundaryId = useBoundaryTerritoryGroupsGroupedByBoundaryGroupSelector();

  const allFillBTGs = useMemo(() => {
    return allFillGroupedByBoundaryId
      .find((group) => group.boundaryGroupId === activeBoundaryTerritoryGroup?.boundaryGroupId)
      ?.boundaryTerritoryGroups
      .filter(notNullsy)
      .sort((btgA, btgB) => {
        if (btgA.boundaryTerritoryGroupId === activeBoundaryTerritoryGroup?.boundaryTerritoryGroupId) {
          return 1;
        }
        if (btgB.boundaryTerritoryGroupId === activeBoundaryTerritoryGroup?.boundaryTerritoryGroupId) {
          return -1;
        }
        return 0;
      });
  }, [allFillGroupedByBoundaryId, activeBoundaryTerritoryGroup?.boundaryGroupId, activeBoundaryTerritoryGroup?.boundaryTerritoryGroupId]);

  const boundaryGroup = useMemo(() => boundaryGroups.find(g => g.id === activeBoundaryTerritoryGroup?.boundaryGroupId),
    [boundaryGroups, activeBoundaryTerritoryGroup?.boundaryGroupId]);

  const selectedMetrics: ReadonlyArray<MetricModel> = useMemo(() => {
    if (!allFillBTGs || !boundaryGroup || !activeBoundaryTerritoryGroup) {
      return [];
    }

    const metricsFromFills: MetricModel[] = allFillBTGs.flatMap(boundaryTerritoryGroup => [
      ...getFillDemographics(boundaryTerritoryGroup.settings, boundaryGroup),
      ...getFillNumericMetrics(boundaryTerritoryGroup.settings, spreadsheetColumns),
      ...getFillGroupMetrics(boundaryTerritoryGroup.settings, spreadsheetColumns),
    ]).reduce((filteredMetrics, metric) => {
      if (filteredMetrics.every(filteredMetric => !areMetricsTheSame(filteredMetric, metric))) {
        filteredMetrics.push(metric);
      }

      return filteredMetrics;
    }, [] as MetricModel[]);

    const savedMetricsWithoutFillMetrics = activeBoundaryTerritoryGroup.settings.metrics
      .map(metric => getBestDemographic(metric, boundaryGroup))
      .filter(savedMetric => !metricsFromFills.some(fillMetric => areMetricsTheSame(savedMetric, fillMetric)));

    return [
      ...metricsFromFills,
      ...savedMetricsWithoutFillMetrics,
    ];
  }, [activeBoundaryTerritoryGroup, boundaryGroup, allFillBTGs, spreadsheetColumns]);

  return selectedMetrics;
};

type BoundarySettingsSubset = Pick<BoundaryTerritoryGroupSettings, 'boundaryTerritoryType' | 'bucketColumnId' | 'demographicId'>;

const getFillDemographics = (btgSettings: BoundarySettingsSubset, boundaryGroup: BoundaryGroup) => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Demographic) {
    return [];
  }

  const demographicFromFill = boundaryGroup.demographics
    .find(d => d.id === btgSettings.demographicId);

  if (demographicFromFill) {
    return [{
      type: SelectedDataType.Demographic,
      demographic: demographicFromFill,
    } as const];
  }

  return [];
};

const getFillNumericMetrics = (btgSettings: BoundarySettingsSubset,
  spreadsheetColumns: ReadonlyArray<SpreadsheetColumn>) => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Numeric) {
    return [];
  }

  const spreadsheetColumn = spreadsheetColumns.find(c => c.id === btgSettings.bucketColumnId);
  if (!spreadsheetColumn) {
    return [];
  }

  const numericActions = [MetricsDataAction.SUM, MetricsDataAction.AVERAGE, MetricsDataAction.HIGH_VALUE,
    MetricsDataAction.LOW_VALUE];
  return numericActions.map(dataAction => ({
    type: SelectedDataType.SpreadsheetColumn,
    spreadsheetColumn,
    dataAction,
  } as const));
};

const getFillGroupMetrics = (btgSettings: BoundarySettingsSubset,
  spreadsheetColumns: ReadonlyArray<SpreadsheetColumn>) => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Groups) {
    return [];
  }

  const spreadsheetColumn = spreadsheetColumns.find(c => c.id === btgSettings.bucketColumnId);
  if (!spreadsheetColumn) {
    return [];
  }

  return [{
    type: SelectedDataType.SpreadsheetColumn,
    spreadsheetColumn,
    dataAction: MetricsDataAction.GROUP_COUNT,
  } as const];
};

const getBestDemographic = (metric: MetricModel, boundaryGroup: BoundaryGroup) => {
  if (metric.type === SelectedDataType.Demographic) {
    const nativeDemographic = boundaryGroup.demographics
      .find(demographic => matchBoundaryGroupDemographic(demographic.id, metric.demographic.id));

    if (nativeDemographic) {
      return {
        ...metric,
        demographic: nativeDemographic,
      };
    }
  }

  return metric;
};
